// Presentation helpers for the license status surface (docs/04-api-contract.md,
// "5a. License status surface"; docs/08-on-prem-readiness.md, "XIV — Offline
// signed licensing"). Everything here is a pure projection of the
// `GET /v1/system/license` response, kept out of the components so the
// banner/page wording and the warn-only semantics are unit-testable in
// isolation. The termination lock is the ONLY state that blocks writes; every
// other warning is warn-and-report and must never imply that work is blocked
// (docs/08, "XIV": "warn + report, never block").
import type { components } from "../api/schema";
import type { BadgeProps } from "../ui";

export type LicenseStatus = components["schemas"]["LicenseStatusResponse"];
export type LicenseState = components["schemas"]["LicenseState"];
export type LicenseWarning = components["schemas"]["LicenseWarning"];
export type LicenseUsageState = components["schemas"]["LicenseUsageState"];
export type LicenseUsageEntry =
  components["schemas"]["LicenseUsageEntryResponse"];
export type LicenseUsage = components["schemas"]["LicenseUsageResponse"];
export type LicenseEntitlements =
  components["schemas"]["LicenseEntitlementsResponse"];

type BadgeTone = NonNullable<BadgeProps["tone"]>;

// The closed `state` enum (docs/04, "5a. License status surface"): a human
// label plus a badge tone. Only
// `locked` reads as danger — a terminated license is the one state where
// writes are actually blocked.
export const LICENSE_STATE_LABELS: Record<LicenseState, string> = {
  not_required: "Not required",
  missing: "Missing",
  active: "Active",
  warning: "Expiring soon",
  grace: "Grace period",
  locked: "Read-only lock",
};

export function licenseStateTone(state: LicenseState): BadgeTone {
  switch (state) {
    case "active":
      return "success";
    case "warning":
    case "grace":
      return "warning";
    case "locked":
    case "missing":
      return "danger";
    case "not_required":
      return "neutral";
  }
}

// The closed per-dimension usage `state` enum (docs/04, "5a. License status
// surface"). warn_80 is an
// informational nudge; warn_95 and exceeded escalate but never block.
export const LICENSE_USAGE_STATE_LABELS: Record<LicenseUsageState, string> = {
  ok: "OK",
  warn_80: "80% used",
  warn_95: "95% used",
  exceeded: "Exceeded",
};

export function licenseUsageStateTone(state: LicenseUsageState): BadgeTone {
  switch (state) {
    case "ok":
      return "success";
    case "warn_80":
    case "warn_95":
      return "warning";
    case "exceeded":
      return "danger";
  }
}

// The metered dimensions carried in `usage` (docs/08, "XIV — Offline signed
// licensing"): seats, agents, and
// the two prepaid pools. Concurrency and environments are entitlement echoes
// only (a ceiling / a contract count), so they carry no usage entry and never
// render a meter. Order is stable so the page reads consistently.
export type LicenseUsageDimensionId = keyof LicenseUsage;

interface LicenseUsageDimensionMeta {
  id: LicenseUsageDimensionId;
  label: string;
  // How the raw numbers read next to the meter (e.g. "1,024 of 2,000 minutes").
}

const USAGE_DIMENSION_ORDER: readonly LicenseUsageDimensionMeta[] = [
  { id: "seats", label: "Seats" },
  { id: "agents", label: "Agents" },
  { id: "prepaid_voice_minutes", label: "Voice minutes" },
  { id: "prepaid_text_conversations", label: "Text conversations" },
];

export interface LicenseUsageDimension extends LicenseUsageDimensionMeta {
  entry: LicenseUsageEntry;
}

// Only dimensions the API actually returns render (UI truthfulness: no meter
// for a null entitlement). A null pool is simply absent from `usage`.
export function licenseUsageDimensions(
  usage: LicenseUsage | null | undefined,
): LicenseUsageDimension[] {
  if (!usage) {
    return [];
  }
  const dimensions: LicenseUsageDimension[] = [];
  for (const usageDimension of USAGE_DIMENSION_ORDER) {
    const entry = usage[usageDimension.id];
    if (entry) {
      dimensions.push({ ...usageDimension, entry });
    }
  }
  return dimensions;
}

export interface LicenseMeter {
  max: number;
  value: number;
}

interface ExactDecimal {
  coefficient: bigint;
  scale: bigint;
}

const METER_SCALE = 1_000_000n;

function exactNonnegativeDecimal(value: string): ExactDecimal | null {
  const match = /^(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(value);
  if (!match) {
    return null;
  }
  const integer = match[1];
  if (!integer) {
    return null;
  }
  const fraction = match[2] ?? "";
  const exponent = Number(match[3] ?? "0");
  if (
    !Number.isSafeInteger(exponent) ||
    Math.abs(exponent) > 1_000 ||
    integer.length + fraction.length > 1_000
  ) {
    return null;
  }
  let coefficient = BigInt(`${integer}${fraction}`);
  const scalePower = fraction.length - exponent;
  if (scalePower < 0) {
    coefficient *= 10n ** BigInt(-scalePower);
    return { coefficient, scale: 1n };
  }
  return { coefficient, scale: 10n ** BigInt(scalePower) };
}

// Meter geometry uses exact decimal/BigInt comparison before projecting into a
// fixed integer display scale. This keeps values immediately below a threshold
// below a full bar instead of rounding them through binary floating point.
// Invalid provider values fail visibly as full usage.
export function licenseUsageMeter(entry: LicenseUsageEntry): LicenseMeter {
  const consumed = exactNonnegativeDecimal(entry.consumed);
  if (!consumed) {
    return { max: Number(METER_SCALE), value: Number(METER_SCALE) };
  }
  if (entry.entitled > 0) {
    const entitlement = BigInt(entry.entitled) * consumed.scale;
    if (consumed.coefficient >= entitlement) {
      return { max: Number(METER_SCALE), value: Number(METER_SCALE) };
    }
    if (consumed.coefficient === 0n) {
      return { max: Number(METER_SCALE), value: 0 };
    }
    const projected =
      (consumed.coefficient * METER_SCALE + entitlement - 1n) / entitlement;
    return {
      max: Number(METER_SCALE),
      value: Number(projected < METER_SCALE ? projected : METER_SCALE - 1n),
    };
  }
  return {
    max: 1,
    value: consumed.coefficient > 0n ? 1 : 0,
  };
}

const USAGE_ATTENTION_STATES: ReadonlySet<LicenseUsageState> = new Set([
  "warn_95",
  "exceeded",
]);

export function isUsageAttentionState(state: LicenseUsageState): boolean {
  return USAGE_ATTENTION_STATES.has(state);
}

export interface LicenseBannerModel {
  tone: "default" | "danger";
  messages: string[];
}

function expiryMessage(status: LicenseStatus): string {
  const days = status.days_until_expiry;
  if (days != null && days > 0) {
    const noun = days === 1 ? "day" : "days";
    return `Your Marker license expires in ${String(days)} ${noun}. Renew before the term ends.`;
  }
  return "Your Marker license is nearing expiry. Renew before the term ends.";
}

// The global banner is shown ONLY when a state needs operator attention:
// state ∈ {warning, grace, locked} or any usage dimension in {warn_95,
// exceeded}. Wording is warn-only for everything but `locked`, which is the
// single state where writes are genuinely blocked.
export function licenseBannerModel(
  status: LicenseStatus | null | undefined,
): LicenseBannerModel | null {
  if (!status) {
    return null;
  }
  const messages: string[] = [];
  let danger = false;

  switch (status.state) {
    case "warning":
      messages.push(expiryMessage(status));
      break;
    case "grace":
      messages.push(
        "Your Marker license has expired and is in its grace period. Access continues normally until the termination date; renew to avoid entering read-only mode.",
      );
      break;
    case "locked":
      danger = true;
      messages.push(
        "Your Marker license has terminated and the app is in read-only mode. You can still sign in and read data; changes stay blocked until the license is renewed.",
      );
      break;
    case "not_required":
    case "missing":
    case "active":
      break;
  }

  for (const dimension of licenseUsageDimensions(status.usage)) {
    const state = dimension.entry.state;
    if (!isUsageAttentionState(state)) {
      continue;
    }
    // Scoped copy only: an absolute "nothing is blocked" would contradict the
    // locked banner above when a terminated license also has an exceeded
    // dimension. The overage itself never blocks; commercial reconciliation is
    // via the signed usage report, not a renewal event (docs/08, "XIV":
    // overage "reconciles commercially via the signed usage report (true-up)").
    if (state === "exceeded") {
      messages.push(
        `${dimension.label} usage has passed your licensed amount. This usage level does not block work; overage is reported for commercial reconciliation.`,
      );
    } else {
      messages.push(
        `${dimension.label} usage is at 95% of your licensed amount. This usage level does not block work; overage is reported for commercial reconciliation.`,
      );
    }
  }

  if (messages.length === 0) {
    return null;
  }
  return { tone: danger ? "danger" : "default", messages };
}
